import React, { useEffect, useState } from "react";
import formsUtil from "../../../../utils/formsUtil";
import { StyledBox, StyledButton, StyledTextField } from "../../../../styled-mui";
import Stack from "@mui/material/Stack";
import { StyledGrid, StyledIconButton } from "@hcl-commerce-store-sdk/react-component";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Checkbox,
  ListItemText,
  Button,
  FormHelperText,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { StoreInputContainer } from "../../input-store/InputStore";
import { swrOptionsSubmitNoRetry } from "../../../../utils/hooks/swr/constants";
import useSWR from "swr";
import demandeService from "../../../../_foundation/apis/transaction/demande.service";
import { userNameSelector, loginStatusSelector } from "../../../../redux/selectors/user";
import { useLocation, useSearchParams } from "react-router-dom";
import { RDV_TYPE } from "../../../../constants/catalog";
import getDisplayName from "react-display-name";
import AsyncCall from "../../../../_foundation/gtm/async.service";
import { CLICK, RDV_TUNNEL_EVENTS } from "../../../../_foundation/constants/gtm";
import { EMPTY_STRING, BOOK_AN_APPOINTMENT_UNIVERS } from "../../../../constants/common";
import { HANDLE_SUCCESS_MESSAGE_ACTION } from "../../../../redux/actions/success";
import { getReCaptchaToken } from "../../../../utils/storeLocatorUtils";
import { StyledFormRdvStore } from "../../prise-de-rdv/form-rdv-store/styled-formRdvStore";
import { AddressAutoSuggest } from "./addressAutoSuggest";
import { PincodeLocater } from "./pinCodeLocater";
import useFetchStaticJson from "../../../../_foundation/hooks/use-static-json";
import { PostalCodeAutoSuggest } from "./postalCodeAutoSuggest";
import { CityAutoSuggest } from "./cityAutoSuggest";

interface AppointmentFormProps {
  formData?: any;
  setFormData?: (data: any) => void;
  onClose?: () => void;
  onSubmitSuccess?: (formData: any, store: any, type?: "APPOINTMENT" | "CALLBACK") => void;
  postalCode?: string;
  isForPartner?: boolean;
  selectedOption?: string;
  onStoreChange?: (store: any) => void;
}

type FieldError = "required" | "invalid" | null;

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  formData,
  setFormData,
  onClose,
  postalCode,
  isForPartner,
  onSubmitSuccess,
  selectedOption,
  onStoreChange,
}) => {
  const widgetName = getDisplayName(AppointmentForm);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const userStatus = useSelector(loginStatusSelector);
  const userNameDetails = useSelector(userNameSelector);

  const [errors, setErrors] = useState<Record<string, FieldError>>({});
  const [firstName, setFirstName] = useState(formData?.prenom ?? (userStatus ? userNameDetails?.firstName : ""));
  const [lastName, setLastName] = useState(formData?.nom ?? (userStatus ? userNameDetails?.lastName : ""));
  const [email, setEmail] = useState(formData?.email ?? (userStatus ? userNameDetails?.email : ""));
  const [zipCode, setZipCode] = useState(formData?.codePostal ?? (userStatus ? userNameDetails?.zipCode : ""));
  const [universe, setUniverse] = useState(formData?.UniversSingle || []);
  const [comment, setComment] = useState(formData?.comment || "");

  // const [firstName, setFirstName] = useState(userStatus ? userNameDetails.firstName : "");
  // const [lastName, setLastName] = useState(userStatus ? userNameDetails.lastName : "");
  // const [email, setEmail] = useState(userStatus ? userNameDetails.email : "");
  // const [zipCode, setZipCode] = useState(postalCode ? postalCode : userStatus ? userNameDetails.zipCode : "");
  const [addressFormData, setAddressFormData] = useState({
    state: EMPTY_STRING,
    country: EMPTY_STRING,
    phone1: userStatus ? userNameDetails.phone1 : EMPTY_STRING,
  });
  // const [universe, setUniverse] = useState<string[]>([]);
  const [tempUniverse, setTempUniverse] = useState<string[]>([]);
  const [openUniverse, setOpenUniverse] = useState(false);
  const [receiveEmail, setReceiveEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [store, setStore] = useState<any>(null);
  // const [comment, setComment] = useState("");
  const [inputValue, setInputValue] = useState(addressFormData.phone1);
  const [address, setAddress] = useState(""); // the address value
  const [errorAddress, setErrorAddress] = useState(false);
  const [cadStores, setCadStores] = useState([]);
  const [ville, setVille] = useState("");
  useEffect(() => {
    setTempUniverse(universe);
  }, [universe]);

  const [cadEligibleStores, setCadEligibleStores] = useState([]);
  const { data /* isLoading, error */ } = useFetchStaticJson({ file: "cad.json", isLocal: false });

  useEffect(() => {
    if (selectedOption === "CAD" && zipCode.length === 5) {
      const availableStores =
        zipCode && zipCode.length === 5 ? data?.cad?.filter((item) => item.cp.includes(zipCode)) : [];

      /* GTM RDV TUNNEL: ERROR, ZIPCODE NOT AVAILABLE FOR DOMICILE SERVICE EVENT */
      if (availableStores && availableStores.length === 0) {
        AsyncCall.sendRdvFormEvent(RDV_TUNNEL_EVENTS.RDV_ERROR_VIEW, {
          step_name: "customer_details",
          error_type: "not_eligible_geo",
          user_postal_code: zipCode || "",
        });
      }

      setCadStores(availableStores);
      setCadEligibleStores(availableStores);
    }
  }, [zipCode]);

  useEffect(() => {
    if (store) {
      onStoreChange?.(store);
    }
  }, [store]);

  const isCadUnavailable = selectedOption === "CAD" && zipCode?.length === 5 && cadEligibleStores.length === 0;

  const handleChangePhone = (phone: string) => {
    setAddressFormData((prev) => ({ ...prev, phone1: phone }));
  };

  const clearError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors: Record<string, FieldError> = {};

    if (!firstName.trim()) newErrors.firstName = "required";
    if (!lastName.trim()) newErrors.lastName = "required";

    if (!email.trim()) newErrors.email = "required";
    else if (!formsUtil.validateEmail(email)) newErrors.email = "invalid";

    if (!addressFormData.phone1.trim()) newErrors.phone1 = "required";
    else if (!formsUtil.validatePhoneNumber(addressFormData.phone1)) newErrors.phone1 = "invalid";

    if (!zipCode.trim()) newErrors.zipCode = "required";
    else if (!formsUtil.validateZipCode(zipCode)) newErrors.zipCode = "invalid";

    if (!store) newErrors.store = "required";

    if (!universe.length) newErrors.universe = "required";

    if (selectedOption === "CAD") {
      if (!address.trim()) newErrors.address = "required";
      if (!ville.trim()) newErrors.ville = "required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
  //   e.preventDefault();
  //   if (!checkForm()) {
  //     setIsSubmitting(false);
  //     return;
  //   }

  //   setIsSubmitting(true);
  //   try {
  //     const token = await getReCaptchaToken("AppointmentForm");
  //     const payload: any = {
  //       type: selectedOption,
  //       nom: lastName,
  //       prenom: firstName,
  //       codePostal: zipCode,
  //       currentUrl: pathname,
  //       telephone: addressFormData.phone1,
  //       email: email,
  //       optin: receiveEmail ? "true" : "false",
  //       codeMag: store?.id,
  //       recaptchaToken: token,
  //       IdProjetPartenaire: searchParams.get("idorigine") || "",
  //       originePartenaire: isForPartner ? "MACIF" : searchParams.get("origine") || "",
  //       Univers: universe,
  //     };

  //     if (comment.trim() !== "") payload.comment = comment.trim();

  //     setFormData(payload);

  //     AsyncCall.sendClick(CLICK.CONTACT_SEND, {
  //       btn_label: t("ChoiceRendezVous.Actions.MakeAppointment"),
  //       form_name: "rdvStoreForm",
  //       email,
  //     });
  //   } catch (err) {
  //     console.error("Submit failed:", err);
  //     setIsSubmitting(false);
  //   }
  // };

  const getUniversePayload = (selected: string[]): string[] => {
    let universe: string[] = ["Menuiseries"];

    const first = selected[0];

    if (first === "Cuisine") {
      universe = ["Cuisines"];
    } else if (first === "SDB") {
      universe = ["SDB"];
    }

    return universe.map((item) => item.replace(/\s+/g, "-"));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    await Promise.resolve();

    setIsSubmitting(true);
    const token = await getReCaptchaToken("appointmentForm");
    const payload: any = {
      type: selectedOption,
      nom: lastName,
      prenom: firstName,
      codePostal: zipCode,
      currentUrl: pathname,
      telephone: addressFormData.phone1,
      email: email,
      optin: receiveEmail ? "true" : "false",
      codeMag: store?.id,
      IdProjetPartenaire: searchParams.get("idorigine") || "",
      originePartenaire: isForPartner ? "MACIF" : searchParams.get("origine") || "",
      Univers: getUniversePayload(universe),
      UniversSingle: universe,
      recaptchaToken: token,
      comment: comment,
    };

    if (selectedOption === "CAD") {
      payload.rue = address;
      payload.ville = ville || "null";
      payload.Profil = "null";
      payload.Realisation = "null";
    }

    if (comment.trim()) {
      payload.comment = comment.trim();
    }

    onSubmitSuccess?.(payload, store, "APPOINTMENT") && onSubmitSuccess(payload, store);

    // optional: reset form
    // setFirstName("");
    // setLastName("");
    // setZipCode("");
    // setStore(null);
    // setEmail("");
    // setAddressFormData({ state: "", country: "", phone1: "" });
    // setInputValue("");
    // setComment("");
    // setReceiveEmail(false);
    // setUniverse([]);
    // setTempUniverse([]);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!formData) return;

    setFirstName(formData.prenom || "");
    setLastName(formData.nom || "");
    setEmail(formData.email || "");
    setZipCode(formData.codePostal || "");
    setUniverse(formData.UniversSingle || []);
    setComment(formData.comment || "");

    const phone = formData.telephone || "";
    setAddressFormData((prev) => ({ ...prev, phone1: phone }));
    setInputValue(phone);
    if (selectedOption === "CAD") {
      setAddress(formData.address || "");
      setVille(formData.ville || "");
    }
  }, [formData, selectedOption]);

  useEffect(() => {
    if (address && errorAddress) {
      setErrorAddress(false);
    }
  }, [address]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^\d+]/g, "");
    if (value === "") {
      setInputValue("");
      handleChangePhone("");
      return;
    }

    if (value.startsWith("0")) {
      value = value
        .replace(/\s/g, "")
        .slice(0, 10)
        .replace(/(\d{2})(?=\d)/g, "$1 ");
    } else if (value.startsWith("+33")) {
      value = value
        .replace(/\s/g, "")
        .replace(/(\+33)(\d)(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5 $6")
        .slice(0, 17);
    } else {
      value = value.replace(/\s/g, "").replace(/(\d{2})(?=\d)/g, "$1 ");
    }

    setInputValue(value);
    handleChangePhone(value);
  };

  const handleSelectUniverse = (value: string) => {
    let updated: string[];

    if (tempUniverse.includes(value)) {
      updated = tempUniverse.filter((v) => v !== value);
    } else {
      updated = [...tempUniverse, value];
    }

    setTempUniverse(updated);
    if (updated.length > 0) {
      clearError("universe");
    }
  };

  const handleSelectAllUniverse = () => {
    const allSelected = tempUniverse.length === BOOK_AN_APPOINTMENT_UNIVERS.length;

    const updated = allSelected ? [] : BOOK_AN_APPOINTMENT_UNIVERS.map((i) => i.value);

    setTempUniverse(updated);
    if (updated.length > 0) {
      clearError("universe");
    }
  };

  const handleRemoveUniverse = (value: string) => {
    const updated = universe.filter((u) => u !== value);
    setUniverse(updated);
    setTempUniverse(updated);
  };

  const handleCallbackRequest = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const token = await getReCaptchaToken("appointmentForm");

      const payload: any = {
        type: selectedOption,
        nom: lastName,
        prenom: firstName,
        codePostal: zipCode,
        currentUrl: pathname,
        telephone: addressFormData.phone1,
        email,
        codeMag: "null",
        Univers: "null",
        optin: receiveEmail ? "true" : "false",
        IdProjetPartenaire: searchParams.get("idorigine") || "",
        originePartenaire: isForPartner ? "MACIF" : searchParams.get("origine") || "",
        recaptchaToken: token,
      };

      // CAD-specific fields
      if (selectedOption === "CAD") {
        payload.rue = address || "null";
        payload.ville = ville || "null";
        payload.Profil = "null";
        payload.Realisation = "null";
      }

      await demandeService.addDemandeRDV({ body: payload });

      onSubmitSuccess?.(payload, "CALLBACK");
    } catch (err) {
      dispatch(
        HANDLE_SUCCESS_MESSAGE_ACTION({
          key: "error-message.CALLBACK_FAILED",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hideActionButtons = selectedOption === "CAD" && zipCode?.length === 5 && cadStores?.length === 0;

  // const { data: formSubmitData } = useSWR(
  //   formData ? { body: { ...formData } } : null,
  //   demandeService.addDemandeRDV,
  //   swrOptionsSubmitNoRetry
  // );

  // useEffect(() => {
  //   if (formSubmitData?.data) {
  //     setFormData(null);
  //     setFirstName("");
  //     setLastName("");
  //     setZipCode("");
  //     setStore(null);
  //     setEmail("");
  //     setAddressFormData({
  //       state: "",
  //       country: "",
  //       phone1: "",
  //     });
  //     setInputValue("");
  //     setComment("");
  //     setReceiveEmail(false);
  //     setUniverse([]);
  //     setTempUniverse([]);

  //     if (isForPartner) {
  //       dispatch(
  //         HANDLE_SUCCESS_MESSAGE_ACTION({
  //           key: "success-message.PARTNER_BENEFITS",
  //         })
  //       );
  //     }

  //     onSubmitSuccess && onSubmitSuccess();
  //     setIsSubmitting(false);
  //   }
  // }, [formSubmitData?.data]);

  return (
    <StyledFormRdvStore>
      <StyledGrid container justifyContent="center">
        <StyledGrid item xs={12}>
          <Typography
            fontSize={14}
            fontWeight={700}
            color="#848484"
            mb={7}
            sx={{
              marginTop: {
                xs: 1,
                md: 0,
              },
            }}>
            Vos informations
          </Typography>
          <form name="rdvStoreForm" id="rdv_store_form" noValidate onSubmit={handleSubmit}>
            <StyledBox p={0} borderRadius={1}>
              {/* First & Last Name */}
              <StyledGrid container spacing={5}>
                <StyledGrid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    pt: {
                      xs: "16px !important",
                      md: "20px !important",
                    },
                  }}>
                  <StyledTextField
                    label={
                      <>
                        Prénom
                        <StyledBox component="span" color="primary.main" sx={{ pl: 1 }}>
                          *
                        </StyledBox>
                      </>
                    }
                    value={firstName}
                    variant="standard"
                    focused
                    autoFocus
                    fullWidth
                    className="styled-v2"
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (e.target.value.trim() !== "") {
                        clearError("firstName");
                      }
                    }}
                    error={Boolean(errors.firstName)}
                    helperText={errors.firstName ? t("ErrorFormMessage.FirstName") : ""}
                  />
                </StyledGrid>
                <StyledGrid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    pt: {
                      xs: "16px !important",
                      md: "20px !important",
                    },
                  }}>
                  <StyledTextField
                    label={
                      <>
                        Nom
                        <StyledBox component="span" color="primary.main" sx={{ pl: 1 }}>
                          *
                        </StyledBox>
                      </>
                    }
                    value={lastName}
                    variant="standard"
                    focused
                    className="styled-v2"
                    fullWidth
                    onFocus={() => clearError("lastName")}
                    onChange={(e) => setLastName(e.target.value)}
                    error={Boolean(errors.lastName)}
                    helperText={errors.lastName === "required" ? t("ErrorFormMessage.LastName") : ""}
                  />
                </StyledGrid>
              </StyledGrid>

              {/* Phone & Email */}
              <StyledGrid
                container
                spacing={5}
                mt={4}
                sx={{
                  "& > .MuiGrid-item": {
                    paddingTop: 0,
                  },
                }}>
                <StyledGrid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    pt: {
                      xs: "4px !important",
                      md: "0px !important",
                    },
                  }}>
                  <StyledTextField
                    label={
                      <>
                        Téléphone
                        <StyledBox component="span" color="primary.main" sx={{ pl: 1 }}>
                          *
                        </StyledBox>
                      </>
                    }
                    value={inputValue}
                    variant="standard"
                    focused
                    className="styled-v2"
                    fullWidth
                    onFocus={() => clearError("phone1")}
                    onChange={handlePhoneChange}
                    error={Boolean(errors.phone1)}
                    helperText={
                      errors.phone1 === "required"
                        ? t("ErrorFormMessage.Phone")
                        : errors.phone1 === "invalid"
                        ? t("ErrorFormMessage.PhoneEmpty")
                        : ""
                    }
                  />
                </StyledGrid>
                <StyledGrid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    pt: {
                      xs: "16px !important",
                      md: "0px !important",
                    },
                  }}>
                  <StyledTextField
                    label={
                      <>
                        Email
                        <StyledBox component="span" color="primary.main" sx={{ pl: 1 }}>
                          *
                        </StyledBox>
                      </>
                    }
                    value={email}
                    variant="standard"
                    focused
                    className="styled-v2"
                    fullWidth
                    onFocus={() => clearError("email")}
                    onChange={(e) => setEmail(e.target.value)}
                    error={Boolean(errors.email)}
                    helperText={
                      errors.email === "required"
                        ? t("ErrorFormMessage.Email")
                        : errors.email === "invalid"
                        ? t("ErrorFormMessage.EmailInvalid")
                        : ""
                    }
                  />
                </StyledGrid>
              </StyledGrid>

              {selectedOption === "CAD" && (
                <StyledGrid container spacing={5} mt={0}>
                  <StyledGrid item xs={12} sm={6}>
                    <AddressAutoSuggest
                      onAddressChange={(val) => {
                        setAddress(val);
                        clearError("address");
                      }}
                      onPostalCodeChange={(postal) => {
                        setZipCode(postal);
                        clearError("zipCode");
                      }}
                      onAreaChange={(area) => {
                        setVille(area);
                      }}
                      error={errors.address === "required"}
                    />
                  </StyledGrid>
                  <StyledGrid item xs={12} sm={6}>
                    <CityAutoSuggest
                      value={ville}
                      onCityChange={(val) => {
                        setVille(val);
                        clearError("ville");
                      }}
                      onPostalCodeChange={(postal) => {
                        setZipCode(postal);
                        clearError("zipCode");
                      }}
                      error={Boolean(errors.ville)}
                    />
                  </StyledGrid>
                </StyledGrid>
              )}

              {/* Zip Code & Store */}
              <StyledGrid
                container
                spacing={5}
                mt={4}
                sx={{
                  "& > .MuiGrid-item": {
                    paddingTop: 0,
                  },
                }}>
                <StyledGrid item xs={12} sm={6}>
                  <PostalCodeAutoSuggest
                    value={zipCode}
                    onAddressChange={(value) => {
                      setZipCode(value);
                      clearError("zipCode");
                    }}
                    onAreaChange={(area) => {
                      setVille(area);
                    }}
                    error={Boolean(errors.zipCode)}
                  />

                  {selectedOption !== "CAD" && (
                    <Box sx={{ padding: "8px 0 1px" }}>
                      <PincodeLocater
                        setZipCode={(pincode) => {
                          setZipCode(pincode);
                          clearError("zipCode");
                        }}
                      />
                    </Box>
                  )}
                </StyledGrid>
                <StyledGrid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    pt: {
                      xs: "16px !important",
                      md: "0px !important",
                    },
                  }}>
                  <StoreInputContainer
                    onChange={(val) => {
                      setStore(val);
                      clearError("store");
                    }}
                    onBlur={() => {
                      /* noop */
                    }}
                    zipCode={zipCode}
                    type="secondary"
                    refStore={store}
                    setRefStore={setStore}
                    error={errors.store === "required"}
                    isFromAppointmentForm
                    selectedOption={selectedOption}
                    cadStores={selectedOption === "CAD" ? cadEligibleStores : undefined}
                    showDistance
                  />
                </StyledGrid>
              </StyledGrid>
              {!hideActionButtons && (
                <>
                  <StyledGrid container spacing={5} mt={0}>
                    {/* Universe Multi-Select */}
                    <StyledGrid item xs={12} sm={6} mt={{ xs: 0, sm: 0 }}>
                      <Box mb={2}>
                        <StyledBox component="label" sx={{ fontSize: "11px", fontWeight: 700, color: "#212121" }}>
                          Univers <span style={{ color: "#D32F2F" }}>*</span>
                        </StyledBox>
                      </Box>

                      <FormControl fullWidth variant="outlined">
                        <Select
                          labelId="universe-multi-select-label"
                          multiple
                          value={openUniverse ? tempUniverse : universe}
                          open={openUniverse}
                          onOpen={() => {
                            setTempUniverse(universe);
                            setOpenUniverse(true);
                          }}
                          onClose={() => {
                            setUniverse(tempUniverse);
                            setOpenUniverse(false);

                            if (tempUniverse.length > 0) {
                              clearError("universe");
                            }
                          }}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E7EAEE",
                              border: "2px solid #E7EAEE",
                            },

                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E7EAEE",
                            },

                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E7EAEE",
                            },

                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E7EAEE",
                            },

                            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E7EAEE",
                            },

                            "& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E7EAEE",
                            },
                            "& .MuiOutlinedInput-root": {
                              padding: "0px",
                              borderRadius: "8px",
                              backgroundColor: "#FFF",

                              "& fieldset": {
                                border: "2px solid #E7EAEE",
                              },

                              "&:hover fieldset": {
                                borderColor: "#E7EAEE",
                              },

                              "&:focused fieldset": {
                                borderColor: "#E7EAEE",
                              },

                              "&.Mui-focused fieldset": {
                                borderColor: "#E7EAEE",
                              },
                            },

                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              padding: "12px 18px",
                              minHeight: "auto",
                            },
                          }}
                          renderValue={(selected) => (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                              {selected.map((value: string) => {
                                const itemTitle = BOOK_AN_APPOINTMENT_UNIVERS.find((i) => i.value === value)?.title;

                                return (
                                  <Chip
                                    key={value}
                                    label={itemTitle}
                                    onDelete={() => handleRemoveUniverse(value)}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    sx={{
                                      fontSize: "12px",
                                      height: "24px",
                                      padding: 0,
                                      backgroundColor: "#F5F5F5",
                                      borderRadius: "4px",

                                      "& .MuiChip-label": {
                                        padding: "2px 5px 2px 10px",
                                        fontWeight: 500,
                                      },

                                      "& .MuiChip-deleteIcon": {
                                        fontSize: "15px",
                                        margin: 0,
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                          MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}>
                          {/* Select All */}
                          {/* <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAllUniverse();
                          }}>
                          <Checkbox
                            checked={tempUniverse.length === BOOK_AN_APPOINTMENT_UNIVERS.length}
                            indeterminate={
                              tempUniverse.length > 0 && tempUniverse.length < BOOK_AN_APPOINTMENT_UNIVERS.length
                            }
                            sx={{
                              color: "#5C87A0",
                              "&.Mui-checked": {
                                color: "#5C87A0",
                              },
                            }}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem> */}

                          {/* Individual Items */}
                          {BOOK_AN_APPOINTMENT_UNIVERS.map((item) => (
                            <MenuItem
                              key={item.value}
                              value={item.value}
                              sx={{
                                "&.Mui-selected": {
                                  backgroundColor: "rgba(92, 135, 160, 0.15)",
                                },
                                "&.Mui-selected:hover": {
                                  backgroundColor: "rgba(92, 135, 160, 0.25)",
                                },
                                "&:hover": {
                                  backgroundColor: "rgba(92, 135, 160, 0.08)",
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectUniverse(item.value);
                              }}>
                              <Checkbox
                                checked={tempUniverse.includes(item.value)}
                                sx={{
                                  color: "#5C87A0",
                                  "&.Mui-checked": {
                                    color: "#5C87A0",
                                  },
                                }}
                              />
                              <ListItemText primary={item.title} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {errors.universe === "required" && (
                        <FormHelperText error>Veuillez sélectionner au moins un univers.</FormHelperText>
                      )}
                    </StyledGrid>
                  </StyledGrid>
                  {/* comment */}
                  <StyledBox
                    sx={{
                      mt: {
                        xs: 10,
                        md: 15,
                      },
                    }}>
                    <StyledTextField
                      variant="standard"
                      focused
                      className="styled-v2"
                      fullWidth
                      label="Commentaire (optionnel)"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      multiline
                      rows={4}
                    />
                  </StyledBox>
                </>
              )}
            </StyledBox>

            {selectedOption === "CAD" && zipCode?.length == 5 && cadStores?.length == 0 && (
              <StyledBox
                mt={4}
                pt={6}
                pr={5}
                pb={6}
                pl={5}
                bgcolor="#F6F9FC"
                color="#333333"
                borderRadius={3}
                textAlign="center">
                <Typography
                  fontSize={20}
                  fontWeight={700}
                  color="#333333"
                  pb={3}
                  m="auto"
                  sx={{ lineHeight: "28px", maxWidth: "520px" }}>
                  Notre conseil à domicile n'est malheureusement pas disponible dans votre ville.
                </Typography>
                <Typography fontSize={15} fontWeight={400} color="#333333" mb={3} sx={{ lineHeight: "21px" }}>
                  Pour être accompagné(e) dans votre projet, un conseiller peut vous rappeler.
                </Typography>
                <StyledButton
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={handleCallbackRequest}
                  disabled={isSubmitting}
                  endIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    backgroundColor: "#C41E3B",
                    "&:hover": {
                      backgroundColor: "#a81832",
                    },
                  }}>
                  être rappelé(e)
                </StyledButton>
              </StyledBox>
            )}

            {/* Action Buttons */}
            {!hideActionButtons && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="right"
                alignItems="center"
                sx={{ pt: { xs: 3, md: 6 } }}
                spacing={1.5}>
                <StyledButton
                  variant="outline"
                  color="black"
                  type="button"
                  sx={{ width: { xs: "100%", sm: "auto" }, display: { xs: "none", md: "inline-flex" } }}
                  onClick={onClose}>
                  Retour
                </StyledButton>

                <StyledButton
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                  sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Choisir mon créneau
                </StyledButton>
              </Stack>
            )}
          </form>
        </StyledGrid>
      </StyledGrid>
    </StyledFormRdvStore>
  );
};

export default AppointmentForm;
